/**
 * Session formatting utilities
 * Follows ADR-003: High Cohesion - Single responsibility: session display formatting
 */

import type { Session } from '../hooks/useSessionList';
import { logWarn } from './logger';

/**
 * Parses a date string robustly handling multiple formats
 */
export function parseSessionDate(dateString: string): Date {
  if (!dateString) return new Date(0);

  if (dateString.includes('/')) {
    const parts = dateString.split(' ');
    if (parts.length >= 2) {
      const [datePart, timePart] = parts;
      const [day, month, year] = datePart.split('/');
      const [hour, minute, second] = timePart.split(':');
      const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${(second || '00').padStart(2, '0')}`;
      return new Date(isoString);
    }
  } else if (dateString.includes('.')) {
    const parts = dateString.split(' ');
    if (parts.length >= 2) {
      const [datePart, timePart] = parts;
      const [day, month, year] = datePart.split('.');
      const [hour, minute, second] = timePart.split('.');
      const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${(second || '00').padStart(2, '0')}`;
      return new Date(isoString);
    }
  }

  return new Date(dateString);
}

/**
 * Formats a session date for display
 */
export function formatSessionDate(
  dateString: string,
  t: (key: string, params?: Record<string, string>) => string
): string {
  if (!dateString) {
    return 'Unknown date';
  }

  const date = parseSessionDate(dateString);

  if (isNaN(date.getTime())) {
    logWarn('Invalid date string:', dateString);
    return 'Invalid date';
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sessionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeWithSeconds = date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  if (sessionDate.getTime() === today.getTime()) {
    return t('time.todayAt', { time: timeWithSeconds });
  } else if (sessionDate.getTime() === yesterday.getTime()) {
    return t('time.yesterdayAt', { time: timeWithSeconds });
  } else {
    const dateStr = date.toLocaleDateString();
    return `${dateStr} ${timeWithSeconds}`;
  }
}

/**
 * Gets the display title for a session
 */
export function getSessionTitle(
  session: Session,
  t: (key: string, params?: Record<string, string>) => string
): string {
  if (session.title && session.title.startsWith('Chat Session ')) {
    const timestampMatch = session.title.match(/Chat Session (.+)/);
    if (timestampMatch) {
      const timestamp = timestampMatch[1];
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return `${t('session.defaultTitle')} ${formatSessionDate(timestamp, t)}`;
      }
    }
  }

  if (session.title && !session.title.startsWith('Chat Session ')) {
    return session.title;
  }

  const dateToUse = session.created_at || session.updated_at;
  if (dateToUse) {
    return `${t('session.defaultTitle')} ${formatSessionDate(dateToUse, t)}`;
  }

  return t('session.defaultTitle');
}

/**
 * Gets the date string for display from a session
 */
export function getSessionDateString(
  session: Session,
  t: (key: string, params?: Record<string, string>) => string
): string {
  let dateToFormat = session.updated_at || session.created_at;

  if (!dateToFormat && session.title?.startsWith('Chat Session ')) {
    const timestampMatch = session.title.match(/Chat Session (.+)/);
    if (timestampMatch) {
      dateToFormat = timestampMatch[1];
    }
  }

  return formatSessionDate(dateToFormat || '', t);
}

